import React, { createContext, useContext, useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import DemoModal from "@/components/marketing/DemoModal";

const MarketingCtx = createContext(null);
export const useMarketing = () => useContext(MarketingCtx);

// Strict three-colour palette — Navy / Gold / Green on white neutral.
const NAVY  = "#0B1E3F";
const GOLD  = "#C8A24B";
const GREEN = "#1F6B4A";

const NAV = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/products", label: "Products", testid: "nav-products" },
  { to: "/use-cases", label: "Use Cases", testid: "nav-use-cases" },
  { to: "/partners", label: "Partners", testid: "nav-partners" },
];

export default function MarketingLayout({ children }) {
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoSource, setDemoSource] = useState("book-a-demo");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  // Scroll smoothly to the top of the page whenever the route changes, so
  // navigating between pages never leaves the user mid-scroll on the new page.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);

  // Pricing CTAs pass ``source="tailored-quote"`` so the sales-inbox email
  // subject reflects the intent. We guard against ``source`` accidentally
  // being a React SyntheticEvent (when handlers do ``onClick={openDemo}``).
  const openDemo = (source) => {
    setDemoSource(typeof source === "string" && source ? source : "book-a-demo");
    setDemoOpen(true);
  };

  return (
    <MarketingCtx.Provider value={{ openDemo, demoSource }}>
      <div className="min-h-screen bg-white text-[#0B1E3F] font-body">
        {/* Nav — white surface, navy ink, gold hover, green pulse */}
        <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b shadow-[0_1px_0_rgba(11,30,63,0.04)]"
                style={{ borderColor: `${NAVY}10` }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5" data-testid="brand-home-link">
              <img src="/vaku-logo.png" alt="Vaku" className="h-10 w-10 rounded-lg object-cover" />
              <div className="leading-none">
                <div className="font-black text-lg tracking-tight" style={{ color: NAVY }}>Vaku</div>
                <div className="text-[9px] font-mono tracking-[0.18em] uppercase mt-1" style={{ color: GOLD }}>
                  AI for business
                </div>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors ${isActive ? "text-[#C8A24B]" : "text-[#0B1E3F]/75 hover:text-[#C8A24B]"}`}>
                  {n.label}
                </NavLink>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-3">
              <Link to="/login" data-testid="nav-login" className="text-sm font-semibold transition-colors"
                    style={{ color: `${NAVY}CC` }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = `${NAVY}CC`)}>
                Login
              </Link>
              <button onClick={openDemo} data-testid="nav-book-demo-button"
                className="rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-colors shadow-[0_4px_14px_rgba(11,30,63,0.20)]"
                style={{ background: NAVY }}
                onMouseEnter={(e) => (e.currentTarget.style.background = GOLD)}
                onMouseLeave={(e) => (e.currentTarget.style.background = NAVY)}>
                Book a Demo
              </button>
            </div>

            <button className="md:hidden" style={{ color: NAVY }}
                    onClick={() => setMobileOpen((o) => !o)} data-testid="nav-mobile-toggle">
              {mobileOpen ? <X size={22} /> : <List size={22} />}
            </button>
          </div>
          {mobileOpen && (
            <div className="md:hidden border-t bg-white px-6 py-4 space-y-3"
                 style={{ borderColor: `${NAVY}10` }}>
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setMobileOpen(false)}
                  className="block text-sm font-medium hover:text-[#C8A24B]"
                  style={{ color: `${NAVY}D9` }} data-testid={`m-${n.testid}`}>{n.label}</NavLink>
              ))}
              <Link to="/login" className="block text-sm font-semibold" style={{ color: `${NAVY}D9` }} data-testid="m-nav-login">Login</Link>
              <button onClick={() => { setMobileOpen(false); openDemo(); }}
                className="w-full rounded-full px-5 py-2.5 text-sm font-semibold text-white"
                style={{ background: NAVY }} data-testid="m-nav-book-demo">Book a Demo</button>
            </div>
          )}
        </header>

        <main>{children}</main>

        {/* Footer — navy surface, gold accents, sparing green dot */}
        <footer className="text-white" style={{ background: NAVY }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-4" style={{ color: GOLD }}>Product</div>
                <ul className="space-y-2 text-sm text-white/80">
                  <li><Link to="/products" className="hover:text-white">Products</Link></li>
                  <li><Link to="/use-cases" className="hover:text-white">Use Cases</Link></li>
                  <li><Link to="/partners" className="hover:text-white">Partners</Link></li>
                  <li><Link to="/login" className="hover:text-white">Login</Link></li>
                </ul>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-4" style={{ color: GOLD }}>Connects to</div>
                <ul className="space-y-2 text-sm text-white/80 font-mono">
                  <li>Oracle Fusion</li><li>Oracle EBS</li><li>Microsoft Dynamics 365</li><li>IFS</li>
                </ul>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-4" style={{ color: GOLD }}>Company</div>
                <ul className="space-y-2 text-sm text-white/80">
                  <li><button onClick={openDemo} className="hover:text-white" data-testid="footer-book-demo">Book a Demo</button></li>
                  <li><a href="mailto:Karthikk@vakuai.com" className="hover:text-white">Karthikk@vakuai.com</a></li>
                </ul>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-4" style={{ color: GOLD }}>Security</div>
                <ul className="space-y-2 text-sm text-white/80">
                  <li className="inline-flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} />
                    AES-256 encryption
                  </li>
                  <li className="inline-flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} />
                    Human-in-the-loop
                  </li>
                  <li className="inline-flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} />
                    Full audit trails
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
              <div className="text-[clamp(3rem,12vw,9rem)] font-black leading-none tracking-tighter" style={{ color: GOLD }}>VĀKU</div>
              <div className="text-xs text-white/40 font-mono">© {new Date().getFullYear()} Vaku · AI for business</div>
            </div>
          </div>
        </footer>

        <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} source={demoSource} />
      </div>
    </MarketingCtx.Provider>
  );
}